import React, { useState, useEffect } from 'react';
import { Snackbar, Alert, AlertTitle, LinearProgress, Box, Typography } from '@mui/material';
import { CloudUpload as CloudUploadIcon, CheckCircle as SuccessIcon, Error as ErrorIcon } from '@mui/icons-material';

/**
 * Component for showing sync notifications for JHA Initial Reports
 * Listens to custom events from jhaSyncService and displays toast notifications
 */
export default function SyncNotifications() {
    const [notification, setNotification] = useState({
        open: false,
        severity: 'info',
        title: '',
        message: '',
        showProgress: false,
        progress: 0,
        autoHideDuration: 6000
    });

    useEffect(() => {
        // Handler for sync start
        const handleSyncStart = (event) => {
            const { total } = event.detail;
            setNotification({
                open: true,
                severity: 'info',
                title: 'Synchronizing Initial Reports',
                message: `Sending ${total} pending initial report${total > 1 ? 's' : ''}...`,
                showProgress: true,
                progress: 0,
                autoHideDuration: null // Don't auto-hide during sync
            });
        };

        // Handler for sync progress
        const handleSyncProgress = (event) => {
            const { current, total } = event.detail;
            const progress = (current / total) * 100;

            setNotification(prev => ({
                ...prev,
                message: `Sending initial report ${current} of ${total}...`,
                progress
            }));
        };

        // Handler for individual item success
        const handleItemSuccess = (event) => {
            const { jobNumber } = event.detail;
            console.log(`Initial report for ${jobNumber} sent successfully`);
        };

        // Handler for sync completion
        const handleSyncComplete = (event) => {
            const { total, success, failed } = event.detail;

            if (failed === 0) {
                // All successful
                setNotification({
                    open: true,
                    severity: 'success',
                    title: 'Sync Complete',
                    message: `Successfully sent ${success} initial report${success > 1 ? 's' : ''}`,
                    showProgress: false,
                    progress: 100,
                    autoHideDuration: 5000
                });
            } else if (success === 0) {
                // All failed
                setNotification({
                    open: true,
                    severity: 'error',
                    title: 'Sync Failed',
                    message: `Failed to send ${failed} initial report${failed > 1 ? 's' : ''}. Will retry when online.`,
                    showProgress: false,
                    progress: 0,
                    autoHideDuration: 7000
                });
            } else {
                // Partial success
                setNotification({
                    open: true,
                    severity: 'warning',
                    title: 'Sync Partially Complete',
                    message: `Sent ${success} of ${total} initial reports. ${failed} failed.`,
                    showProgress: false,
                    progress: (success / total) * 100,
                    autoHideDuration: 6000
                });
            }
        };

        // Handler for sync error
        const handleSyncError = (event) => {
            const { error } = event.detail;
            setNotification({
                open: true,
                severity: 'error',
                title: 'Sync Error',
                message: `An error occurred: ${error}`,
                showProgress: false,
                progress: 0,
                autoHideDuration: 7000
            });
        };

        // Handler for missing-required-documents (backend returned 422).
        // Показывает юзеру список документов которые надо добавить, с причиной.
        const handleMissingDocuments = (event) => {
            const { kind, missing = [], message } = event.detail || {};
            const items = missing.slice(0, 5).map(m => m.document_name || '').filter(Boolean).join(', ');
            const more = missing.length > 5 ? ` (+${missing.length - 5} more)` : '';
            const label = kind === 'initial-report' ? 'Initial report not sent' : 'JHA not sent';
            setNotification({
                open: true,
                severity: 'warning',
                title: label,
                message: `${message || 'Required documents missing.'} Missing: ${items}${more}. Open the JHA → Document step, then attach a photo or fill the form for each.`,
                showProgress: false,
                progress: 0,
                autoHideDuration: 10000,
            });
        };

        // Add event listeners
        window.addEventListener('initialReportSyncStart', handleSyncStart);
        window.addEventListener('initialReportSyncProgress', handleSyncProgress);
        window.addEventListener('initialReportSyncItemSuccess', handleItemSuccess);
        window.addEventListener('initialReportSyncComplete', handleSyncComplete);
        window.addEventListener('initialReportSyncError', handleSyncError);
        window.addEventListener('jhaMissingDocuments', handleMissingDocuments);

        // Cleanup
        return () => {
            window.removeEventListener('initialReportSyncStart', handleSyncStart);
            window.removeEventListener('initialReportSyncProgress', handleSyncProgress);
            window.removeEventListener('initialReportSyncItemSuccess', handleItemSuccess);
            window.removeEventListener('initialReportSyncComplete', handleSyncComplete);
            window.removeEventListener('initialReportSyncError', handleSyncError);
            window.removeEventListener('jhaMissingDocuments', handleMissingDocuments);
        };
    }, []);

    const handleClose = (event, reason) => {
        // Don't close on clickaway during sync
        if (reason === 'clickaway' && notification.showProgress) {
            return;
        }
        setNotification(prev => ({ ...prev, open: false }));
    };

    const getIcon = () => {
        if (notification.showProgress) {
            return <CloudUploadIcon />;
        }
        if (notification.severity === 'success') {
            return <SuccessIcon />;
        }
        if (notification.severity === 'error') {
            return <ErrorIcon />;
        }
        return null;
    };

    return (
        <Snackbar
            open={notification.open}
            autoHideDuration={notification.autoHideDuration}
            onClose={handleClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
            <Alert
                onClose={handleClose}
                severity={notification.severity}
                icon={getIcon()}
                sx={{
                    minWidth: 350,
                    boxShadow: 3
                }}
            >
                <AlertTitle sx={{ fontWeight: 600 }}>
                    {notification.title}
                </AlertTitle>
                <Typography variant="body2" sx={{ mb: notification.showProgress ? 1 : 0 }}>
                    {notification.message}
                </Typography>
                {notification.showProgress && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: '100%' }}>
                            <LinearProgress
                                variant="determinate"
                                value={notification.progress}
                                sx={{ height: 6, borderRadius: 1 }}
                            />
                        </Box>
                        <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right' }}>
                            {Math.round(notification.progress)}%
                        </Typography>
                    </Box>
                )}
            </Alert>
        </Snackbar>
    );
}
